import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';
import RecareCampaignsPage from './RecareCampaignsPage';

// `recare-campaigns` legacy hub key now lands here on the AI Auto tab.
const LEGACY_TAB_MAP = { 'recare-campaigns': 'aiAuto' };

const RecallPage = ({ API_BASE, patientsList, initialTab = 'due' }) => {
  const [tab, setTab] = useState(LEGACY_TAB_MAP[initialTab] || initialTab); // due | overdue | all | create | aiAuto
  const [recalls, setRecalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ patientId:'', type:'Routine Check-up', dueDate:'', notes:'' });
  const [stats, setStats] = useState({ due:0, overdue:0, contacted:0 });

  const fetchRecalls = async (endpoint) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/recalls${endpoint}`);
      setRecalls(r.data);
    } catch (e) { setRecalls([]); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const [due, overdue, all] = await Promise.all([
        axios.get(`${API_BASE}/recalls/due-this-week`),
        axios.get(`${API_BASE}/recalls/overdue`),
        axios.get(`${API_BASE}/recalls?status=contacted`),
      ]);
      setStats({ due: due.data.length, overdue: overdue.data.length, contacted: all.data.length });
    } catch (e) {}
  };

  useEffect(() => {
    fetchStats();
    fetchRecalls('/due-this-week');
  }, []); // eslint-disable-line

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'due')     fetchRecalls('/due-this-week');
    if (t === 'overdue') fetchRecalls('/overdue');
    if (t === 'all')     fetchRecalls('?status=due');
  };

  const handleCreate = async () => {
    if (!form.patientId || !form.dueDate) return alert('Select a patient and due date');
    try {
      await axios.post(`${API_BASE}/recalls`, form);
      setForm({ patientId:'', type:'Routine Check-up', dueDate:'', notes:'' });
      setTab('due');
      fetchRecalls('/due-this-week');
      fetchStats();
    } catch (e) { alert('Failed to create recall'); }
  };

  const handleStatus = async (id, status) => {
    await axios.put(`${API_BASE}/recalls/${id}`, { status });
    fetchRecalls(tab === 'due' ? '/due-this-week' : tab === 'overdue' ? '/overdue' : '?status=due');
    fetchStats();
  };

  const handleWhatsApp = async (recall) => {
    const pt = recall.patientId;
    if (!pt?.phone) return alert('No phone number');
    const msg = `Hello ${pt.name}, this is a reminder from Siddharth Dental Clinic. Your ${recall.type} is due. Please schedule an appointment at your earliest convenience. Thank you!`;
    window.open(`https://web.whatsapp.com/send?phone=91${pt.phone}&text=${encodeURIComponent(msg)}`, '_blank');
    // Log contact
    await axios.post(`${API_BASE}/recalls/${recall._id}/contact`, {
      channel: 'whatsapp',
      content: msg,
    });
    fetchRecalls(tab === 'due' ? '/due-this-week' : tab === 'overdue' ? '/overdue' : '?status=due');
    fetchStats();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recall?')) return;
    await axios.delete(`${API_BASE}/recalls/${id}`);
    fetchRecalls(tab === 'due' ? '/due-this-week' : tab === 'overdue' ? '/overdue' : '?status=due');
    fetchStats();
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const isOverdue = d => d && new Date(d) < new Date();

  const statusColors = {
    due:       { bg:'#fef3c7', color:'#92400e' },
    contacted: { bg:'#dbeafe', color:'#1e40af' },
    scheduled: { bg:'#d1fae5', color:'#065f46' },
    completed: { bg:'#e5e7eb', color:'#374151' },
    cancelled: { bg:'#fee2e2', color:'#991b1b' },
  };

  const tabBtn = (key, label, count) => (
    <button onClick={()=>handleTabChange(key)}
      style={{
        padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer',
        fontWeight:'600', fontSize:'0.86rem',
        backgroundColor: tab===key ? theme.primary : '#e9ecef',
        color: tab===key ? 'white' : '#555',
      }}>
      {label} {count > 0 && <span style={{
        marginLeft:'6px', padding:'1px 8px', borderRadius:'10px', fontSize:'0.72rem', fontWeight:'700',
        backgroundColor: tab===key ? 'rgba(255,255,255,0.3)' : theme.danger+'20',
        color: tab===key ? 'white' : theme.danger,
      }}>{count}</span>}
    </button>
  );

  return (
    <div>
      {/* KPI cards */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px', marginBottom:'20px'}}>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Due This Week</span>
          <h2 style={{margin:'4px 0 0', color:theme.warning, fontSize:'1.6rem', fontWeight:'700'}}>{stats.due}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Overdue</span>
          <h2 style={{margin:'4px 0 0', color:theme.danger, fontSize:'1.6rem', fontWeight:'700'}}>{stats.overdue}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Contacted</span>
          <h2 style={{margin:'4px 0 0', color:theme.info, fontSize:'1.6rem', fontWeight:'700'}}>{stats.contacted}</h2>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap'}}>
        {tabBtn('due', 'Due This Week', stats.due)}
        {tabBtn('overdue', 'Overdue', stats.overdue)}
        {tabBtn('all', 'All Pending', 0)}
        <button onClick={()=>setTab('create')}
          style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
            backgroundColor: tab==='create' ? theme.success : '#e9ecef',
            color: tab==='create' ? 'white' : '#555'}}>
          + Create Recall
        </button>
        <button onClick={()=>setTab('aiAuto')}
          style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
            backgroundColor: tab==='aiAuto' ? theme.primary : '#e9ecef',
            color: tab==='aiAuto' ? 'white' : '#555'}}>
          AI Auto Campaigns
        </button>
      </div>

      {/* AI Auto — RecareCampaignsPage embedded as a tab */}
      {tab === 'aiAuto' && <RecareCampaignsPage API_BASE={API_BASE} />}

      {/* Create Form */}
      {tab === 'create' && (
        <div style={{...cardStyle, marginBottom:'20px', borderTop:`4px solid ${theme.success}`}}>
          <h3 style={{margin:'0 0 16px', color:theme.navy}}>Create New Recall</h3>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:'14px', marginBottom:'14px'}}>
            <div>
              <label style={labelStyle}>Patient *</label>
              <select value={form.patientId} onChange={e=>setForm({...form, patientId:e.target.value})} style={inputStyle}>
                <option value="">Select patient...</option>
                {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Recall Type</label>
              <select value={form.type} onChange={e=>setForm({...form, type:e.target.value})} style={inputStyle}>
                <option>Routine Check-up</option>
                <option>Post-RCT Follow-up</option>
                <option>Scaling Recall</option>
                <option>Crown Review</option>
                <option>Implant Review</option>
                <option>Orthodontic Review</option>
                <option>Post-Surgery</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date *</label>
              <input type="date" value={form.dueDate} onChange={e=>setForm({...form, dueDate:e.target.value})} style={inputStyle}/>
            </div>
          </div>
          <div style={{marginBottom:'14px'}}>
            <label style={labelStyle}>Notes</label>
            <input value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} placeholder="Optional notes..." style={inputStyle}/>
          </div>
          <button onClick={handleCreate} style={{...btnStyle, marginTop:0, backgroundColor:theme.success}}>Create Recall</button>
        </div>
      )}

      {/* Recall List */}
      {tab !== 'create' && (
        <div style={cardStyle}>
          {loading ? <p style={{color:theme.textMuted}}>Loading...</p> :
           recalls.length === 0 ? (
            <div style={{textAlign:'center', padding:'50px', color:'#bbb'}}>
              <p style={{fontSize:'1.2rem', margin:'0 0 6px'}}>✓</p>
              <p>No recalls in this category</p>
            </div>
          ) : (
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={thStyle}>Patient</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Attempts</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recalls.map(r => {
                  const sc = statusColors[r.status] || statusColors.due;
                  const overdue = isOverdue(r.dueDate) && r.status !== 'completed' && r.status !== 'cancelled';
                  return (
                    <tr key={r._id} style={{backgroundColor: overdue ? '#fff5f5' : 'transparent'}}>
                      <td style={tdStyle}><strong>{r.patientId?.name || '—'}</strong></td>
                      <td style={tdStyle}>{r.patientId?.phone || '—'}</td>
                      <td style={tdStyle}>{r.type}</td>
                      <td style={{...tdStyle, color: overdue ? theme.danger : theme.navy, fontWeight: overdue ? '700' : '400'}}>
                        {fmtDate(r.dueDate)}
                        {overdue && <span style={{marginLeft:'6px', fontSize:'0.7rem', color:theme.danger}}>OVERDUE</span>}
                      </td>
                      <td style={tdStyle}>
                        <span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700',
                          backgroundColor:sc.bg, color:sc.color}}>
                          {r.status}
                        </span>
                      </td>
                      <td style={tdStyle}>{r.contactAttempts || 0}</td>
                      <td style={tdStyle}>
                        <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                          <button onClick={()=>handleWhatsApp(r)}
                            style={{padding:'4px 10px', backgroundColor:'#25D366', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                            💬 WhatsApp
                          </button>
                          {r.status !== 'completed' && (
                            <button onClick={()=>handleStatus(r._id, 'completed')}
                              style={{padding:'4px 10px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                              ✓ Done
                            </button>
                          )}
                          {r.status !== 'cancelled' && (
                            <button onClick={()=>handleStatus(r._id, 'cancelled')}
                              style={{padding:'4px 10px', backgroundColor:'#6b7785', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                              Cancel
                            </button>
                          )}
                          <button onClick={()=>handleDelete(r._id)}
                            style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default RecallPage;
